import type { SubscriptionResponse } from './types'

/**
 * The plan a trial runs on when nothing else was chosen, and that an account which predates the
 * signup plan picker still holds. It is NOT how a trial is recognised — see {@link isTrialOnly}.
 */
export const TRIAL_PLAN_CODE = 'trial'

/** The facts that say whether an account has only ever been on a free trial. */
export type TrialFacts = Pick<SubscriptionResponse, 'trialEndsAt' | 'firstPaidAt'>

/**
 * True when the account was given a trial and has never paid for anything, so it owes nothing and
 * has no subscription to restart.
 *
 * Both halves matter. Without "was given a trial", a `legacy` account — never billed, so never paid —
 * would be described as a lapsed trial. Without "never paid", an account that converted and then
 * failed a payment would be.
 */
export function isTrialOnly(sub: TrialFacts): boolean {
  return !!sub.trialEndsAt && !sub.firstPaidAt
}

/**
 * A lapsed trial: degraded because the free trial ran out, not because a payment failed. There is no
 * balance to settle, so it must never be worded as one.
 *
 * This used to be `status === 'PastDue' && planCode === TRIAL_PLAN_CODE`, on the reasoning that an
 * account which never chose a plan is still on the trial plan. The signup plan picker ended that:
 * every new account trials INSIDE a real plan, so a lapsed trial is PastDue on `solo` or `growth`,
 * this returned false, and the customer was told to settle a balance that never existed. The fact is
 * now read (`firstPaidAt`) rather than inferred from the plan's name, and it matches the backend's
 * `Entitlement.IsTrialEnded` exactly.
 */
export function isTrialEnded(sub: Pick<SubscriptionResponse, 'status'> & TrialFacts): boolean {
  return (
    (sub.status === 'PastDue' || sub.status === 'Suspended' || sub.status === 'Canceled') &&
    isTrialOnly(sub)
  )
}

/**
 * Whole days left in a running trial, rounded up so "ends later today" reads as 1, not 0. Null when
 * the end date is absent or unparseable (it is not guaranteed just because the status is Trialing).
 * Zero or negative means the end has passed but the expiry job has not moved the account yet.
 *
 * The same arithmetic as the backend's `TrialReminderRule.DaysLeft`, so the banner and the
 * "your trial ends in N days" notice can never disagree about the number.
 */
export function trialDaysLeft(trialEndsAt: string | null | undefined, now: Date = new Date()): number | null {
  if (!trialEndsAt) return null
  const ends = new Date(trialEndsAt).getTime()
  if (Number.isNaN(ends)) return null
  return Math.ceil((ends - now.getTime()) / 86_400_000)
}
